import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const LEAD_SCORE_COLORS = {
  HOT: 'bg-red-500',
  WARM: 'bg-amber-500',
  COLD: 'bg-slate-500'
};

const LEAD_SCORE_TEXT = {
  HOT: 'text-red-400',
  WARM: 'text-amber-400',
  COLD: 'text-slate-400'
};

export default function OpsDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({
    leadScore: '',
    contacted: '',
    search: ''
  });
  const [contactNotes, setContactNotes] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ops_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('ops_token');
    const userData = localStorage.getItem('ops_user');
    if (!token || !userData) {
      navigate('/ops');
      return false;
    }
    setUser(JSON.parse(userData));
    return true;
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/ops/stats', { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ops_token');
        localStorage.removeItem('ops_user');
        navigate('/ops');
      }
    }
  }, [getAuthHeaders, navigate]);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.leadScore) params.append('lead_score', filters.leadScore);
      if (filters.contacted !== '') params.append('contacted', filters.contacted);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/api/ops/leads?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setLeads(response.data.leads);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ops_token');
        localStorage.removeItem('ops_user');
        navigate('/ops');
      } else {
        setError('Failed to load leads');
      }
    }
  }, [filters, getAuthHeaders, navigate]);

  const fetchLeadDetail = async (leadId) => {
    try {
      const response = await api.get(`/api/ops/leads/${leadId}`, { 
        headers: getAuthHeaders() 
      });
      setSelectedLead(response.data);
    } catch (err) {
      setError('Failed to load lead details');
    }
  };

  const markAsContacted = async (leadId) => {
    try {
      await api.patch(`/api/ops/leads/${leadId}/contact`, 
        { notes: contactNotes },
        { headers: getAuthHeaders() }
      );
      setContactNotes('');
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) {
        fetchLeadDetail(leadId);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ops_token');
        localStorage.removeItem('ops_user');
        navigate('/ops');
      } else {
        setError('Failed to mark as contacted');
      }
    }
  };

  const unmarkAsContacted = async (leadId) => {
    try {
      await api.patch(`/api/ops/leads/${leadId}/uncontact`, {}, { 
        headers: getAuthHeaders() 
      });
      fetchLeads();
      fetchStats();
      if (selectedLead?.id === leadId) {
        fetchLeadDetail(leadId);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('ops_token');
        localStorage.removeItem('ops_user');
        navigate('/ops');
      } else {
        setError('Failed to update lead');
      }
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.leadScore) params.append('lead_score', filters.leadScore);
      if (filters.contacted !== '') params.append('contacted', filters.contacted);
      
      const response = await api.get(`/api/ops/leads/export/csv?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fmiq_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export CSV');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ops_token');
    localStorage.removeItem('ops_user');
    navigate('/ops');
  };

  useEffect(() => {
    if (checkAuth()) {
      setLoading(true);
      Promise.all([fetchStats(), fetchLeads()]).finally(() => setLoading(false));
    }
  }, [checkAuth, fetchStats, fetchLeads]);

  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [filters, user, fetchLeads]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">FMiQ Operations</h1>
              <p className="text-sm text-slate-400">Lead Management Dashboard</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-slate-300 text-sm">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
              data-testid="ops-logout"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
            <button onClick={() => setError('')} className="float-right">×</button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Total Leads</p>
              <p className="text-2xl font-bold text-white">{stats.total_leads}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Hot 🔥</p>
              <p className="text-2xl font-bold text-red-400">{stats.by_score?.hot || 0}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Warm</p>
              <p className="text-2xl font-bold text-amber-400">{stats.by_score?.warm || 0}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Cold</p>
              <p className="text-2xl font-bold text-slate-400">{stats.by_score?.cold || 0}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Contacted</p>
              <p className="text-2xl font-bold text-green-400">{stats.contacted}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">Last 7 Days</p>
              <p className="text-2xl font-bold text-blue-400">{stats.recent_7_days}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <input
              type="text"
              placeholder="Search org, name, or email..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="flex-1 min-w-[200px] px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="ops-search"
            />
            <select
              value={filters.leadScore}
              onChange={(e) => setFilters(f => ({ ...f, leadScore: e.target.value }))}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="ops-filter-score"
            >
              <option value="">All Scores</option>
              <option value="HOT">Hot</option>
              <option value="WARM">Warm</option>
              <option value="COLD">Cold</option>
            </select>
            <select
              value={filters.contacted}
              onChange={(e) => setFilters(f => ({ ...f, contacted: e.target.value }))}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="ops-filter-contacted"
            >
              <option value="">All Status</option>
              <option value="true">Contacted</option>
              <option value="false">Not Contacted</option>
            </select>
            <button
              onClick={exportCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center space-x-2"
              data-testid="ops-export-csv"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Organization</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider">Rating</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-700/30 transition" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-white">{lead.contact_name || 'Unknown'}</p>
                        <p className="text-sm text-slate-400">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white">{lead.org_name || '-'}</p>
                      <p className="text-sm text-slate-400">{lead.portfolio_size || '-'} facilities</p>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-xl font-bold text-white">{lead.overall_score?.toFixed(2) || '-'}</span>
                      <span className="text-slate-400">/5</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${LEAD_SCORE_COLORS[lead.lead_score]} text-white`}>
                        {lead.lead_score || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {lead.contacted ? (
                        <div className="text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                            ✓ Contacted
                          </span>
                          <p className="text-xs text-slate-500 mt-1">by {lead.contacted_by}</p>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-500/20 text-slate-400">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-400 text-sm">
                      {lead.created_at?.split('T')[0]}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => fetchLeadDetail(lead.id)}
                        className="px-3 py-1.5 text-sm bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition mr-2"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        View
                      </button>
                      {!lead.contacted && (
                        <button
                          onClick={() => markAsContacted(lead.id)}
                          className="px-3 py-1.5 text-sm bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition"
                          data-testid={`contact-lead-${lead.id}`}
                        >
                          Mark Contacted
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                      No leads found matching your criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setSelectedLead(null)}>
          <div className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedLead.org_name || 'Lead Details'}</h2>
                <p className="text-slate-400">{selectedLead.contact_name} • {selectedLead.email}</p>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-slate-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Phone</p>
                  <p className="text-white font-medium">{selectedLead.phone || '-'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Job Title</p>
                  <p className="text-white font-medium">{selectedLead.job_title || '-'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Portfolio</p>
                  <p className="text-white font-medium">{selectedLead.portfolio_size || '-'} facilities</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Primary Goal</p>
                  <p className="text-white font-medium">{selectedLead.primary_goal || '-'}</p>
                </div>
              </div>

              {/* Scores */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <span className={`w-3 h-3 rounded-full ${LEAD_SCORE_COLORS[selectedLead.lead_score]} mr-2`}></span>
                  Overall Score: {selectedLead.rollup?.overall_readiness_index?.toFixed(2)}/5.00
                  <span className={`ml-3 px-3 py-1 rounded-full text-sm ${LEAD_SCORE_COLORS[selectedLead.lead_score]} text-white`}>
                    {selectedLead.lead_score}
                  </span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(selectedLead.rollup?.category_scores || {}).map(([cat, data]) => (
                    <div key={cat} className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-400 mb-1">{cat.replace(/_/g, ' ').replace(/readiness/gi, '').trim()}</p>
                      <p className="text-xl font-bold text-white">{data.final_score?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Services */}
              {selectedLead.recommended_services?.primary?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Recommended Services</h3>
                  <div className="space-y-2">
                    {selectedLead.recommended_services.primary.map((svc, i) => (
                      <div key={i} className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                        <p className="font-medium text-amber-400">{svc.name}</p>
                        <p className="text-sm text-slate-300">{svc.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {selectedLead.ai_insights?.enhanced_executive_summary && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">AI Analysis</h3>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-slate-300 text-sm leading-relaxed">{selectedLead.ai_insights.enhanced_executive_summary}</p>
                    {selectedLead.ai_insights.total_potential_savings && (
                      <p className="mt-3 text-green-400 font-medium">
                        💰 {selectedLead.ai_insights.total_potential_savings}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Status */}
              <div className="border-t border-slate-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Contact Status</h3>
                {selectedLead.contacted ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-green-400 font-medium">✓ Contacted by {selectedLead.contacted_by}</p>
                    <p className="text-slate-400 text-sm">{selectedLead.contacted_at?.split('T')[0]}</p>
                    {selectedLead.contact_notes && (
                      <p className="mt-2 text-slate-300 text-sm">Notes: {selectedLead.contact_notes}</p>
                    )}
                    <button
                      onClick={() => unmarkAsContacted(selectedLead.id)}
                      className="mt-3 px-4 py-2 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition"
                    >
                      Undo Contact Status
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <textarea
                      value={contactNotes}
                      onChange={(e) => setContactNotes(e.target.value)}
                      placeholder="Add notes about your contact (optional)..."
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={3}
                    />
                    <button
                      onClick={() => markAsContacted(selectedLead.id)}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium"
                    >
                      Mark as Contacted
                    </button>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-700">
                <a
                  href={`mailto:${selectedLead.email}?subject=Your%20FMiQ%20Readiness%20Assessment&body=Hi%20${selectedLead.contact_name || 'there'},%0A%0AThank%20you%20for%20completing%20the%20FM%20Readiness%20Assessment.`}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email Lead</span>
                </a>
                {selectedLead.phone && (
                  <a
                    href={`tel:${selectedLead.phone}`}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Call</span>
                  </a>
                )}
                <a
                  href={`/results/${selectedLead.id}?unlock=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition flex items-center space-x-2"
                  data-testid="view-full-report-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <span>View Full Report (Unlocked)</span>
                </a>
                <button
                  onClick={async () => {
                    try {
                      const response = await api.post(`/api/quickscore/${selectedLead.id}/export/pdf?unlock=true`, {}, {
                        responseType: 'blob',
                        headers: getAuthHeaders()
                      });
                      const url = window.URL.createObjectURL(response.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `FMiQ_Full_Report_${selectedLead.id.slice(0, 8)}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (err) {
                      alert('Failed to download PDF');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition flex items-center space-x-2"
                  data-testid="download-full-pdf-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download Full PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
